/**
 * Phones keep an open tab (or a home-screen app) in memory for days and show
 * it again without loading the page, so a player could go on using a build
 * from before the last deploy. When the app comes back on screen it asks the
 * server for index.html and, if that names a different main script or
 * stylesheet than the one running (a deploy may change only the CSS), reloads.
 * The slip lives in sessionStorage and survives this.
 */

const MAIN_BUNDLE = /\/static\/(?:js|css)\/main\.[0-9a-f]+\.(?:js|css)/g;
// Not more often than this, so quick app switches do not each fetch.
const MIN_GAP_MS = 60_000;

// The main script and stylesheet the page runs, or that index.html names.
function bundles(html: string): string {
  return Array.from(new Set(html.match(MAIN_BUNDLE) ?? []))
    .sort()
    .join(' ');
}

function runningBundle(): string {
  const urls = [
    ...Array.from(document.scripts).map((s) => s.src),
    ...Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
    ).map((l) => l.href),
  ];
  return bundles(urls.join(' '));
}

export function startAppVersionCheck() {
  const running = runningBundle();
  // Dev server: no hashed bundle, nothing to compare.
  if (!running) return;
  let lastCheck = Date.now();
  let checking = false;

  const check = async () => {
    if (checking || Date.now() - lastCheck < MIN_GAP_MS) return;
    checking = true;
    lastCheck = Date.now();
    try {
      const res = await fetch('/index.html', { cache: 'no-store' });
      if (!res.ok) return;
      const served = bundles(await res.text());
      if (served && served !== running) window.location.reload();
    } catch {
      // Offline: try again next time.
    } finally {
      checking = false;
    }
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
  // Safari's back-forward cache restores a page without any visibility change.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      lastCheck = 0;
      check();
    }
  });
  window.addEventListener('focus', check);
}
